import type { TokenCredential } from "@azure/core-auth";
import * as secrets from "./secrets.js";
import * as jwt from "./jwt.js";

export interface CheckOutcome {
  ok: boolean;
  error?: string;
  value?: string;
  slug?: string;
  installationsCount?: number;
  count?: number;
}

export interface CheckResult {
  ok: boolean; // true iff every individual check passed
  transient?: boolean; // true iff ok is false and the failure is transient (KV 429/5xx/403, GitHub 5xx/429/network)
  checks: {
    kvSecretsReadable: CheckOutcome;
    appIdNumeric: CheckOutcome;
    privateKeyValid: CheckOutcome;
    appJwtAccepted: CheckOutcome;
    installationsListable: CheckOutcome;
  };
}

function emptyChecks(): CheckResult["checks"] {
  return {
    kvSecretsReadable: { ok: false },
    appIdNumeric: { ok: false },
    privateKeyValid: { ok: false },
    appJwtAccepted: { ok: false },
    installationsListable: { ok: false },
  };
}

function isTransientErr(e: unknown): boolean {
  const any = e as { statusCode?: number; status?: number; message?: string };
  const code = any.statusCode ?? any.status;
  if (typeof code === "number" && (code === 429 || code === 403 || code >= 500)) return true;
  return /throttl|timeout|econn|network|aborted|forbidden|propagat/i.test(any.message ?? "");
}

function isTransientHttpStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

/**
 * Sanity-check the platform-level GitHub App configuration. Zero side
 * effects. Used by both the `m8t brain check-app` command and as an inline
 * silent-if-pass precondition in the link/create/unlink cascades.
 *
 * Validates:
 *   1. The three KV secrets (id, slug, private-key) are present + readable.
 *   2. App ID parses as a positive integer.
 *   3. Private key parses as a valid RSA PEM (signAppJwt succeeds).
 *   4. GET /app accepts the App JWT and returns a slug matching KV.
 *   5. GET /app/installations succeeds (HTTP 200 with an array).
 */
async function runChecksOnce(args: { credential: TokenCredential; kvUri: string }): Promise<CheckResult> {
  const checks = emptyChecks();

  // Check 1: KV secrets readable.
  let appId: string, slug: string, privateKeyPem: string;
  try {
    const s = await secrets.readAppSecrets({ credential: args.credential, kvUri: args.kvUri });
    appId = s.appId;
    slug = s.slug;
    privateKeyPem = s.privateKeyPem;
    checks.kvSecretsReadable = { ok: true };
  } catch (e) {
    checks.kvSecretsReadable = { ok: false, error: (e as Error).message };
    return { ok: false, transient: isTransientErr(e), checks };
  }

  // Check 2: appId numeric.
  const appIdNum = Number(appId);
  if (Number.isInteger(appIdNum) && appIdNum > 0) {
    checks.appIdNumeric = { ok: true, value: appId };
  } else {
    checks.appIdNumeric = { ok: false, error: `not a positive integer: '${appId}'` };
    return { ok: false, transient: false, checks };
  }

  // Check 3: private key valid (signAppJwt throws if not).
  let appJwt: string;
  try {
    appJwt = jwt.signAppJwt({ appId, privateKeyPem });
    checks.privateKeyValid = { ok: true };
  } catch (e) {
    checks.privateKeyValid = { ok: false, error: (e as Error).message };
    return { ok: false, transient: false, checks };
  }

  // Check 4: GET /app accepts the App JWT; slug matches.
  try {
    const res = await fetch("https://api.github.com/app", {
      headers: {
        Authorization: `Bearer ${appJwt}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
      },
    });
    const text = await res.text();
    if (!res.ok) {
      checks.appJwtAccepted = { ok: false, error: `HTTP ${String(res.status)}: ${text.slice(0, 200)}` };
      return { ok: false, transient: isTransientHttpStatus(res.status), checks };
    }
    const data = JSON.parse(text) as { slug?: string; installations_count?: number };
    if (data.slug !== slug) {
      checks.appJwtAccepted = {
        ok: false,
        error: `slug mismatch: KV says '${slug}', App says '${data.slug ?? ""}'`,
        slug: data.slug,
        installationsCount: data.installations_count,
      };
      return { ok: false, transient: false, checks };
    }
    checks.appJwtAccepted = {
      ok: true,
      slug: data.slug,
      installationsCount: data.installations_count,
    };
  } catch (e) {
    checks.appJwtAccepted = { ok: false, error: (e as Error).message };
    return { ok: false, transient: isTransientErr(e), checks };
  }

  // Check 5: GET /app/installations returns 200 with an array.
  try {
    const res = await fetch("https://api.github.com/app/installations", {
      headers: {
        Authorization: `Bearer ${appJwt}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
      },
    });
    if (!res.ok) {
      checks.installationsListable = { ok: false, error: `HTTP ${String(res.status)}` };
      return { ok: false, transient: isTransientHttpStatus(res.status), checks };
    }
    const arr = (await res.json()) as unknown[];
    checks.installationsListable = { ok: true, count: arr.length };
  } catch (e) {
    checks.installationsListable = { ok: false, error: (e as Error).message };
    return { ok: false, transient: isTransientErr(e), checks };
  }

  return { ok: true, transient: false, checks };
}

export async function checkAppHealth(args: {
  credential: TokenCredential;
  kvUri: string;
  retries?: number;
  sleepMs?: number;
}): Promise<CheckResult> {
  const retries = args.retries ?? 3;
  const sleepMs = args.sleepMs ?? 1500;
  let last: CheckResult = { ok: false, transient: false, checks: emptyChecks() };
  for (let i = 0; i < retries; i++) {
    last = await runChecksOnce(args);
    if (last.ok || !last.transient) return last; // success, or a permanent misconfig — no point retrying
    if (sleepMs > 0 && i < retries - 1) await new Promise<void>((r) => setTimeout(r, sleepMs));
  }
  return last;
}
